import { cn } from '@costar-ui/cosmos'
import { reactNodeToString } from '@costar-ui/utils'
import { flexRender } from '@tanstack/react-table'
import React, { useCallback, useMemo, useState } from 'react'
import type { ReactNode, SyntheticEvent } from 'react'

import evaluateCellScrollWidth from './evaluate-cell-scroll-width'
import Cell from './primitives/cell'
import { useTableContext } from './primitives/table-context-provider'
import type { ColumnMeta, TableCellProps } from './types'

function TableCell<TDate, TValue = ReactNode>(
  props: TableCellProps<TDate, TValue>
) {
  const { className, cell, isMobile, ...rest } = props

  const meta: ColumnMeta = cell.column.columnDef.meta || {}
  const header = cell.column.columnDef.header
  //@ts-ignore
  const headerText = typeof header === 'function' ? header() : header
  const metaClassName = isMobile ? '' : meta.className
  const isSticky = !isMobile && meta.className?.includes('sticky')
  const fixLeft = isSticky ? cell.column.getStart() : 0
  const width = cell.column.getSize()
  const [showTitle, setShowTitle] = useState(false)
  const { fillContainer } = useTableContext()
  const isFillContainerDummyColumn =
    cell.id === 'fillContainerColumn' && fillContainer === 'append'
  const handleOnMouseOver = useCallback(
    (e: SyntheticEvent<HTMLTableCellElement>) => {
      e.stopPropagation()
      const cellElement = e.target as HTMLElement
      const compare = !!cellElement && evaluateCellScrollWidth(cellElement)
      setShowTitle(compare)
    },
    []
  )
  const cellStyle: React.CSSProperties = useMemo(
    () =>
      isMobile || isFillContainerDummyColumn
        ? {}
        : {
            width,
            ...(fixLeft > 0 ? { left: fixLeft, zIndex: 10 } : {})
          },
    [isMobile, isFillContainerDummyColumn, width, fixLeft]
  )

  const title =
    showTitle && typeof cell.column.columnDef.cell === 'function'
      ? reactNodeToString(cell.column.columnDef.cell(cell.getContext()))
      : ''

  return (
    <Cell
      className={cn(className, metaClassName)}
      data-cell={reactNodeToString(headerText)}
      style={cellStyle}
      title={title}
      onMouseOver={handleOnMouseOver}
      {...rest}
    >
      <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
    </Cell>
  )
}

export default TableCell
